<template>
  <ValidationObserver
    v-slot="{
      invalid
    }"
    :class="$style.verticalTabContainer"
    tag="div"
  >
    <PageScrolling
      hasWideBreakpoint
      :sections="tabs"
      shouldDetectScrolling
    >
      <template v-slot:#password>
        <SectionPassword
          v-model="orgSettings"
        />
      </template>
      <template v-slot:#passwordrecoveryemail>
        <SectionPasswordRecoveryEmail
          v-model="orgSettings"
        />
      </template>
      <template v-slot:#uidgid>
        <SectionUidGidManagement
          v-model="orgSettings"
        />
      </template>
      <template v-slot:#passwordconfig>
        <SectionPasswordConfiguration
          v-model="orgSettings"
        />
        <LegalNotice />
      </template>
      <template v-slot:footer>
        <Container>
          <ButtonGroupSave
            :isDisabledPrimary="invalid || !enableMainAction"
            :isFullWidth="false"
            :isLoadingPrimary="loading"
            :secondaryButtonProps="$options.secondaryButtonProps"
            @primaryAction="handleSavePassword"
            @secondaryAction="handleCancel"
          />
        </Container>
      </template>
    </PageScrolling>
    <ModalUndoChanges
      :showModal="showModalUndo"
      @cancel="handleCloseModalUndo"
      @close="handleCloseModalUndo"
      @save="handleContinueUndo"
    />
    <ModalPasswordRequirements
      :showEmailMessage="true"
      :showModal="showModalPassword"
      :showSmallOrganizationMessage="isSmallOrg"
      @cancel="handleCloseModalPassword"
      @close="handleCloseModalPassword"
      @save="handleContinuePassword"
    />
  </ValidationObserver>
</template>

<script>
import {
  ButtonGroupSave, Container, PageScrolling, Types,
} from '@jumpcloud/ui-components';
import { ValidationObserver } from 'vee-validate3';
import { featureFlags } from 'client/store/modules/FeatureFlagsModule';
import { mapActions, mapGetters, mapState } from 'vuex';
import LegalNotice from 'client/settings/components/LegalNotice.vue';
import ModalPasswordRequirements from 'client/settings/Security/ModalPasswordRequirements.vue';
import ModalUndoChanges from 'client/settings/ModalUndoChanges.vue';
import SectionPassword from 'client/settings/Security/SectionPassword.vue';
import SectionPasswordConfiguration from 'client/settings/Security/SectionPasswordConfiguration.vue';
import SectionPasswordRecoveryEmail from 'client/settings/Security/SectionPasswordRecoveryEmail.vue';
import SectionUidGidManagement from 'client/settings/Security/SectionUidGidManagement.vue';
import SettingsMixin from 'client/settings/SettingsMixin';

const { passwordResetViaRecoveryEmail } = featureFlags;

const { TabItem } = Types;
const settingsModel = 'SettingsModel';
const systemUserCollection = 'SystemUserCollection';

export default {
  name: 'SecurityPage',

  components: {
    ButtonGroupSave,
    Container,
    LegalNotice,
    ModalPasswordRequirements,
    ModalUndoChanges,
    PageScrolling,
    SectionPassword,
    SectionPasswordConfiguration,
    SectionPasswordRecoveryEmail,
    SectionUidGidManagement,
    ValidationObserver,
  },

  mixins: [SettingsMixin],

  data: () => ({
    showModalPassword: false,
  }),

  computed: {
    ...mapState('FeatureFlagsModule', {
      [passwordResetViaRecoveryEmail]: state => state.betaFeatures[passwordResetViaRecoveryEmail],
    }),

    ...mapGetters(systemUserCollection, {
      systemUserCount: 'getTotalCountOfAllItems',
    }),

    isSmallOrg() {
      const smallOrgThreshold = 300;
      return this.systemUserCount < smallOrgThreshold;
    },

    tabs() {
      return [
        TabItem({
          key: '#password',
          label: 'Password Settings',
        }),
        TabItem({
          key: '#passwordrecoveryemail',
          label: 'Password Recovery Email',
        }),
        TabItem({
          key: '#uidgid',
          label: 'UID/GID Management',
        }),
        TabItem({
          key: '#passwordconfig',
          label: 'Password Configurations',
        }),
      ];
    },
  },

  methods: {
    ...mapActions(settingsModel, [
      'updatePasswordPolicy',
    ]),

    handleCloseModalPassword() {
      this.showModalPassword = false;
    },

    handleContinuePassword(date) {
      this.showModalPassword = false;
      this.updatePasswordPolicy({
        effectiveDate: new Date().toISOString(),
        gracePeriodDate: date.toISOString(),
      });

      this.handleSave();
    },

    handleSavePassword() {
      const currentPasswordPolicy = this.previousItem.passwordPolicy;
      const pendingPasswordPolicy = this.item.passwordPolicy;
      const passwordMinLengthOrComplexityIncreased = (
        parseInt(pendingPasswordPolicy.minLength, 10) > currentPasswordPolicy.minLength)
        || ((pendingPasswordPolicy.needsLowercase && !currentPasswordPolicy.needsLowercase)
        || (pendingPasswordPolicy.needsUppercase && !currentPasswordPolicy.needsUppercase)
        || (pendingPasswordPolicy.needsNumeric && !currentPasswordPolicy.needsNumeric)
        || (pendingPasswordPolicy.needsSymbolic && !currentPasswordPolicy.needsSymbolic)
        );

      if (passwordMinLengthOrComplexityIncreased) {
        this.showModalPassword = true;
      } else {
        this.handleSave();
      }
    },
  },
};
</script>

<style module>
@import '../../components/PageLayouts/tabbedPageLayout.css';
@import '../settings.css';
</style>
